import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type Props = {
  scrollRef: React.RefObject<HTMLElement | null>;
  hostRef?: React.RefObject<HTMLElement | null>;
  clipRef?: React.RefObject<HTMLElement | null>;
  inset?: number;
};
type Metrics = { height: number; top: number; left: number; trackTop: number; trackHeight: number };

const TRACK_WIDTH = 8;
const THUMB_MIN_HEIGHT = 24;

export const ScrollbarOverlay: React.FC<Props> = ({ scrollRef, hostRef, clipRef, inset = 0 }) => {
  const [metrics, setMetrics] = useState<Metrics>({ height: 0, top: 0, left: 0, trackTop: 0, trackHeight: 0 });
  const [hovered, setHovered] = useState(false);
  const [scrolling, setScrolling] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const measure = useCallback(() => {
    const element = scrollRef.current;
    const host = hostRef?.current ?? element?.parentElement;
    if (!element || !host || element.scrollHeight <= element.clientHeight) {
      setMetrics((previous) => previous.height === 0 ? previous : { ...previous, height: 0 });
      return;
    }
    const rect = element.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();
    const clipRect = clipRef?.current?.getBoundingClientRect();
    const hostTop = hostRect.top + host.clientTop;
    const hostLeft = hostRect.left + host.clientLeft;
    const hostRight = hostLeft + host.clientWidth;
    const hostBottom = hostTop + host.clientHeight;
    const clipTop = clipRect?.top ?? rect.top;
    const clipBottom = clipRect?.bottom ?? rect.bottom;
    const clipRight = clipRect?.right ?? rect.right;
    const trackTop = Math.max(rect.top, clipTop, hostTop) - hostTop + inset;
    const trackBottom = Math.min(rect.bottom, clipBottom, hostBottom) - hostTop - inset;
    const trackHeight = Math.max(0, Math.min(element.clientHeight, trackBottom - trackTop));
    if (trackHeight <= 0) {
      setMetrics((previous) => previous.height === 0 ? previous : { ...previous, height: 0 });
      return;
    }
    const height = Math.min(
      trackHeight,
      Math.max(THUMB_MIN_HEIGHT, (trackHeight * trackHeight) / element.scrollHeight),
    );
    const maxTop = Math.max(0, trackHeight - height);
    const maxScroll = element.scrollHeight - element.clientHeight;
    const rawTop = maxScroll > 0 ? (element.scrollTop / maxScroll) * maxTop : 0;
    const top = Math.max(0, Math.min(rawTop, maxTop));
    const rawLeft = Math.min(rect.right, clipRight, hostRight) - hostLeft - TRACK_WIDTH - 1;
    const left = Math.max(0, Math.min(rawLeft, Math.max(0, host.clientWidth - TRACK_WIDTH)));
    setMetrics({
      height,
      top,
      left,
      trackTop,
      trackHeight,
    });
  }, [clipRef, hostRef, inset, scrollRef]);

  const markScrolling = useCallback(() => {
    setScrolling(true);
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setScrolling(false);
      timerRef.current = null;
    }, 700);
  }, []);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    const host = hostRef?.current ?? element?.parentElement;
    if (!element || !host) return;
    const handleScroll = () => { measure(); markScrolling(); };
    measure();
    const frameId = requestAnimationFrame(measure);
    element.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    resizeObserver?.observe(element);
    if (host !== element) resizeObserver?.observe(host);
    if (clipRef?.current && clipRef.current !== element && clipRef.current !== host) {
      resizeObserver?.observe(clipRef.current);
    }
    const mutationObserver = typeof MutationObserver !== 'undefined' ? new MutationObserver(measure) : null;
    mutationObserver?.observe(element, { childList: true, characterData: true, subtree: true });
    return () => {
      cancelAnimationFrame(frameId);
      element.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [clipRef, hostRef, markScrolling, measure, scrollRef]);

  useEffect(() => () => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
  }, []);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const element = scrollRef.current;
    if (!element || metrics.height <= 0) return;
    event.preventDefault();
    const startY = event.clientY;
    const startScroll = element.scrollTop;
    const maxTop = Math.max(0, metrics.trackHeight - metrics.height);
    const maxScroll = element.scrollHeight - element.clientHeight;
    if (maxTop <= 0 || maxScroll <= 0) return;
    const move = (moveEvent: PointerEvent) => {
      const nextScroll = startScroll + ((moveEvent.clientY - startY) / maxTop) * maxScroll;
      element.scrollTop = Math.max(0, Math.min(nextScroll, maxScroll));
      measure();
      markScrolling();
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up, { once: true });
  };

  if (typeof document === 'undefined' || metrics.height <= 0) return null;
  return (
    <div
      className={`rte-scrollbar-overlay${hovered || scrolling ? ' rte-scrollbar-overlay-visible' : ''}`}
      style={{ top: metrics.trackTop, left: metrics.left, height: metrics.trackHeight }}
      onMouseEnter={() => { measure(); setHovered(true); }}
      onMouseMove={measure}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="rte-scrollbar-overlay-thumb"
        style={{ height: metrics.height, transform: `translateY(${metrics.top}px)` }}
        onPointerDown={handlePointerDown}
      />
    </div>
  );
};
